/**
 * Minimal hand-written runtime validation combinators (no external dependencies).
 *
 * A `Validator<T>` checks an unknown value and records issues with JSON-path-like locations. Object
 * validators are strict by default (unknown keys are rejected) so that clients cannot smuggle data into
 * stored documents.
 *
 * Every combinator also records a JSON Schema (draft 2020-12) description of what it accepts (`schema`), so the
 * protocol can publish a machine-readable API description (./describe.ts, `system.describe`, GET /api/schema) from
 * the same validators the server runs: clients on other platforms (Android, iOS) generate their models from it.
 * Checks JSON Schema cannot express (cross-field sums, JSON byte sizes) are stated in `description`.
 */
import { LONE_SURROGATE, checkJsonData, utf8Length, wellFormedText } from '../json';

export interface Issue {
  path: string;
  message: string;
}

/** A JSON Schema (draft 2020-12) object; only plain JSON values inside. */
export interface JsonSchema {
  [keyword: string]: unknown;
}

/** The JSON Schema dialect of `Validator.schema` and of the published API description. */
export const JSON_SCHEMA_DIALECT = 'https://json-schema.org/draft/2020-12/schema';

export interface Validator<T> {
  readonly kind: string;
  readonly optional?: boolean;
  /** JSON Schema of the accepted values (absent only on hand-made validators: then "any value"). */
  readonly schema?: JsonSchema;
  check(value: unknown, path: string, issues: Issue[]): boolean;
  /** Phantom type marker (never set at runtime). */
  readonly _type?: T;
}

export interface OptionalValidator<T> extends Validator<T | undefined> {
  readonly optional: true;
}

export type Infer<V> = V extends Validator<infer T> ? T : never;
type OptionalKeys<S> = { [K in keyof S]: S[K] extends { optional: true } ? K : never }[keyof S];
type RequiredKeys<S> = Exclude<keyof S, OptionalKeys<S>>;
export type InferObject<S> = { [K in RequiredKeys<S>]: Infer<S[K]> } & { [K in OptionalKeys<S>]?: Exclude<Infer<S[K]>, undefined> };

function mk<T>(kind: string, check: (value: unknown, path: string, issues: Issue[]) => boolean, schema: JsonSchema = {}): Validator<T> {
  return { kind, check, schema };
}

/** JSON Schema `pattern` of a RegExp (ECMA-262 syntax, as JSON Schema uses; our patterns carry no flags). */
function patternOf(re: RegExp): string {
  return re.source;
}

/** `schema` with `extra` added as one more `allOf` member (keeps the existing keywords). */
function withAllOf(schema: JsonSchema, extra: JsonSchema): JsonSchema {
  const allOf = Array.isArray(schema.allOf) ? [...(schema.allOf as JsonSchema[]), extra] : [extra];
  return { ...schema, allOf };
}

function withDescription(schema: JsonSchema, text: string): JsonSchema {
  return { ...schema, description: typeof schema.description === 'string' ? `${schema.description}; ${text}` : text };
}

function stringSchema(opts: StringOptions): JsonSchema {
  const out: JsonSchema = { type: 'string' };
  if (opts.min !== undefined) out.minLength = opts.min;
  if (opts.max !== undefined) out.maxLength = opts.max;
  const patterns: string[] = [];
  if (opts.pattern) patterns.push(patternOf(opts.pattern));
  // String.prototype.trim removes exactly what \s matches (WhiteSpace and LineTerminator).
  if (opts.trimmed) patterns.push('^(?:\\S(?:[\\s\\S]*\\S)?)?$');
  if (patterns.length === 1) out.pattern = patterns[0];
  else if (patterns.length > 1) out.allOf = patterns.map((pattern) => ({ pattern }));
  // One character class of what the string may not contain: control characters and lone surrogates (under the u
  // flag that JSON Schema patterns use, a surrogate pair is one code point outside U+D800..U+DFFF).
  const banned = `${(opts.noControl ?? true) ? '\\u0000-\\u001f\\u007f' : ''}${(opts.wellFormed ?? true) ? '\\ud800-\\udfff' : ''}`;
  if (banned !== '') out.not = { pattern: `[${banned}]` };
  return out;
}

function rangeSchema(type: 'integer' | 'number', opts: { min?: number; max?: number }): JsonSchema {
  const out: JsonSchema = { type };
  if (opts.min !== undefined) out.minimum = opts.min;
  if (opts.max !== undefined) out.maximum = opts.max;
  return out;
}

/**
 * JSON Schema (draft 2020-12) of a validator: a fresh plain-JSON copy, safe to serialize and to modify. A validator
 * without a recorded schema (hand-made) is described as "any value" with its kind.
 */
export function toJsonSchema(v: Validator<unknown>): JsonSchema {
  return JSON.parse(JSON.stringify(v.schema ?? { description: `any value (${v.kind})` })) as JsonSchema;
}

function describe(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  return typeof v;
}

export interface StringOptions {
  min?: number;
  max?: number;
  pattern?: RegExp;
  /** Reject control characters (default true). */
  noControl?: boolean;
  /**
   * Reject lone UTF-16 surrogates (default true): half of an emoji is not text, and strict JSON decoders of other
   * platforms (Swift, Kotlin serialization, serde_json; I-JSON, RFC 7493) refuse a whole message that carries one. Only
   * a field whose shared rules refuse it with their own reason (the typed nickname, password, clan name and
   * description) turns it off.
   */
  wellFormed?: boolean;
  /** Require the string to equal its trimmed form. */
  trimmed?: boolean;
}

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

export const s = {
  string(opts: StringOptions = {}): Validator<string> {
    return mk('string', (v, path, issues) => {
      if (typeof v !== 'string') return issues.push({ path, message: `expected string, got ${describe(v)}` }), false;
      if (opts.min !== undefined && v.length < opts.min) return issues.push({ path, message: `must have at least ${opts.min} characters` }), false;
      if (opts.max !== undefined && v.length > opts.max) return issues.push({ path, message: `must have at most ${opts.max} characters` }), false;
      if ((opts.noControl ?? true) && CONTROL.test(v)) return issues.push({ path, message: 'must not contain control characters' }), false;
      if ((opts.wellFormed ?? true) && LONE_SURROGATE.test(v)) return issues.push({ path, message: 'must be well-formed UTF-16 (a lone surrogate is not text)' }), false;
      if (opts.trimmed && v.trim() !== v) return issues.push({ path, message: 'must not start or end with whitespace' }), false;
      if (opts.pattern && !opts.pattern.test(v)) return issues.push({ path, message: `must match ${opts.pattern}` }), false;
      return true;
    }, stringSchema(opts));
  },

  int(opts: { min?: number; max?: number } = {}): Validator<number> {
    return mk('int', (v, path, issues) => {
      if (typeof v !== 'number' || !Number.isInteger(v)) return issues.push({ path, message: `expected integer, got ${describe(v)}` }), false;
      if (opts.min !== undefined && v < opts.min) return issues.push({ path, message: `must be >= ${opts.min}` }), false;
      if (opts.max !== undefined && v > opts.max) return issues.push({ path, message: `must be <= ${opts.max}` }), false;
      return true;
    }, rangeSchema('integer', opts));
  },

  number(opts: { min?: number; max?: number } = {}): Validator<number> {
    return mk('number', (v, path, issues) => {
      if (typeof v !== 'number' || !Number.isFinite(v)) return issues.push({ path, message: `expected finite number, got ${describe(v)}` }), false;
      if (opts.min !== undefined && v < opts.min) return issues.push({ path, message: `must be >= ${opts.min}` }), false;
      if (opts.max !== undefined && v > opts.max) return issues.push({ path, message: `must be <= ${opts.max}` }), false;
      return true;
    }, rangeSchema('number', opts));
  },

  boolean(): Validator<boolean> {
    return mk('boolean', (v, path, issues) => (typeof v === 'boolean' ? true : (issues.push({ path, message: `expected boolean, got ${describe(v)}` }), false)), { type: 'boolean' });
  },

  literal<L extends string | number | boolean>(...values: L[]): Validator<L> {
    return mk(
      'literal',
      (v, path, issues) =>
        (values as unknown[]).includes(v) ? true : (issues.push({ path, message: `expected one of ${values.map((x) => JSON.stringify(x)).join(', ')}` }), false),
      values.length === 1 ? { const: values[0] } : { enum: [...values] },
    );
  },

  null(): Validator<null> {
    return mk('null', (v, path, issues) => (v === null ? true : (issues.push({ path, message: `expected null, got ${describe(v)}` }), false)), { type: 'null' });
  },

  optional<T>(inner: Validator<T>): OptionalValidator<T> {
    return { kind: 'optional', optional: true, schema: inner.schema, check: (v, path, issues) => v === undefined || inner.check(v, path, issues) };
  },

  nullable<T>(inner: Validator<T>): Validator<T | null> {
    return mk('nullable', (v, path, issues) => v === null || inner.check(v, path, issues), { anyOf: [toJsonSchema(inner), { type: 'null' }] });
  },

  union<A, B>(a: Validator<A>, b: Validator<B>): Validator<A | B> {
    return mk('union', (v, path, issues) => {
      const ia: Issue[] = [];
      if (a.check(v, path, ia)) return true;
      const ib: Issue[] = [];
      if (b.check(v, path, ib)) return true;
      issues.push(...(ia.length <= ib.length ? ia : ib));
      return false;
    }, { anyOf: [toJsonSchema(a), toJsonSchema(b)] });
  },

  array<T>(item: Validator<T>, opts: { min?: number; max?: number } = {}): Validator<T[]> {
    return mk('array', (v, path, issues) => {
      if (!Array.isArray(v)) return issues.push({ path, message: `expected array, got ${describe(v)}` }), false;
      if (opts.min !== undefined && v.length < opts.min) return issues.push({ path, message: `must have at least ${opts.min} items` }), false;
      if (opts.max !== undefined && v.length > opts.max) return issues.push({ path, message: `must have at most ${opts.max} items` }), false;
      let ok = true;
      for (let i = 0; i < v.length; i++) {
        if (!item.check(v[i], `${path}[${i}]`, issues)) {
          ok = false;
          if (issues.length > 20) break;
        }
      }
      return ok;
    }, arraySchema(item, opts));
  },

  record<T>(value: Validator<T>, opts: { maxKeys?: number; keyPattern?: RegExp } = {}): Validator<Record<string, T>> {
    return mk('record', (v, path, issues) => {
      if (typeof v !== 'object' || v === null || Array.isArray(v)) return issues.push({ path, message: `expected object, got ${describe(v)}` }), false;
      const keys = Object.keys(v);
      if (opts.maxKeys !== undefined && keys.length > opts.maxKeys) return issues.push({ path, message: `must have at most ${opts.maxKeys} keys` }), false;
      let ok = true;
      for (const k of keys) {
        // "__proto__" as an own key (JSON.parse makes one) would set the prototype of any object it is copied into.
        if (k === '__proto__') {
          issues.push({ path: `${path}.${k}`, message: 'reserved key' });
          ok = false;
          continue;
        }
        if (opts.keyPattern && !opts.keyPattern.test(k)) {
          issues.push({ path: `${path}.${k}`, message: `key must match ${opts.keyPattern}` });
          ok = false;
          continue;
        }
        if (LONE_SURROGATE.test(k)) {
          issues.push({ path: `${path}.${k}`, message: 'key must be well-formed UTF-16 (a lone surrogate is not text)' });
          ok = false;
          continue;
        }
        if (!value.check((v as Record<string, unknown>)[k], `${path}.${k}`, issues)) ok = false;
      }
      return ok;
    }, recordSchema(value, opts));
  },

  object<S extends Record<string, Validator<unknown>>>(shape: S, opts: { strict?: boolean } = {}): Validator<InferObject<S>> {
    const strict = opts.strict ?? true;
    return mk('object', (v, path, issues) => {
      if (typeof v !== 'object' || v === null || Array.isArray(v)) return issues.push({ path, message: `expected object, got ${describe(v)}` }), false;
      const o = v as Record<string, unknown>;
      let ok = true;
      for (const [k, val] of Object.entries(shape)) {
        if (o[k] === undefined && !val.optional) {
          issues.push({ path: `${path}.${k}`, message: 'is required' });
          ok = false;
          continue;
        }
        if (!val.check(o[k], `${path}.${k}`, issues)) ok = false;
      }
      if (strict) {
        for (const k of Object.keys(o)) {
          // Own members of the shape only: a name inherited from Object.prototype ("constructor", "__proto__",
          // "toString"...) is an unknown field like any other, not a declared one whose value would go unchecked.
          if (!Object.hasOwn(shape, k)) {
            issues.push({ path: `${path}.${k}`, message: 'unknown field' });
            ok = false;
          }
        }
      }
      return ok;
    }, objectSchema(shape, strict));
  },

  /**
   * Arbitrary JSON data, bounded in serialized size and depth; its strings and member names are well-formed UTF-16
   * (checkJsonData: no lone surrogate, which strict JSON decoders of other platforms refuse with the whole message).
   */
  json(opts: { maxBytes?: number; maxDepth?: number } = {}): Validator<import('../json').JsonValue> {
    const limits = [
      opts.maxBytes !== undefined ? `at most ${opts.maxBytes} bytes as UTF-8 JSON` : null,
      `nesting depth at most ${opts.maxDepth ?? 64}`,
      'no lone UTF-16 surrogate in its strings and member names',
    ].filter(Boolean);
    return mk('json', (v, path, issues) => {
      if (v === undefined) return issues.push({ path, message: 'is required' }), false;
      const err = checkJsonData(v, opts.maxDepth ?? 64, path);
      if (err) return issues.push({ path, message: err }), false;
      if (opts.maxBytes !== undefined) {
        const size = utf8Length(JSON.stringify(v));
        if (size > opts.maxBytes) return issues.push({ path, message: `is ${size} bytes, limit is ${opts.maxBytes}` }), false;
      }
      return true;
    }, { description: `any JSON value (${limits.join(', ')})` });
  },

  /**
   * Custom check returning an error message or null. `schema`: the JSON Schema of the accepted values (as precise
   * as JSON Schema allows; default: "any value" described by `kind`).
   */
  custom<T>(kind: string, fn: (value: unknown) => string | null, schema?: JsonSchema): Validator<T> {
    return mk(
      kind,
      (v, path, issues) => {
        const err = fn(v);
        if (err) return issues.push({ path, message: err }), false;
        return true;
      },
      schema ?? { description: `any value (${kind})` },
    );
  },

  /**
   * Adds a cross-field refinement to an existing validator. `doc` describes it in the JSON Schema: `schema` (added
   * as an `allOf` member) where JSON Schema can express the rule, `description` where it cannot.
   */
  refine<T>(base: Validator<T>, fn: (value: T) => string | null, doc: { description?: string; schema?: JsonSchema } = {}): Validator<T> {
    let schema = base.schema ?? {};
    if (doc.schema) schema = withAllOf(schema, doc.schema);
    if (doc.description) schema = withDescription(schema, doc.description);
    return mk(
      base.kind,
      (v, path, issues) => {
        if (!base.check(v, path, issues)) return false;
        const err = fn(v as T);
        if (err) return issues.push({ path, message: err }), false;
        return true;
      },
      schema,
    );
  },
};

function arraySchema(item: Validator<unknown>, opts: { min?: number; max?: number }): JsonSchema {
  const out: JsonSchema = { type: 'array', items: toJsonSchema(item) };
  if (opts.min !== undefined) out.minItems = opts.min;
  if (opts.max !== undefined) out.maxItems = opts.max;
  return out;
}

function recordSchema(value: Validator<unknown>, opts: { maxKeys?: number; keyPattern?: RegExp }): JsonSchema {
  const out: JsonSchema = { type: 'object', additionalProperties: toJsonSchema(value) };
  if (opts.maxKeys !== undefined) out.maxProperties = opts.maxKeys;
  // A key pattern names the allowed keys; without one, any key but one with a lone surrogate (s.record's check).
  if (opts.keyPattern) out.propertyNames = { pattern: patternOf(opts.keyPattern) };
  else out.propertyNames = { not: { pattern: '[\\ud800-\\udfff]' } };
  return out;
}

function objectSchema(shape: Record<string, Validator<unknown>>, strict: boolean): JsonSchema {
  const properties: Record<string, JsonSchema> = {};
  const required: string[] = [];
  for (const [k, v] of Object.entries(shape)) {
    properties[k] = toJsonSchema(v);
    if (!v.optional) required.push(k);
  }
  const out: JsonSchema = { type: 'object', properties };
  if (required.length > 0) out.required = required;
  if (strict) out.additionalProperties = false;
  return out;
}

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; issues: Issue[] };

/**
 * Validates `value`. The issues go back to the client in the error (details.issues), so their paths, which name the
 * payload's own member names, are made well-formed text (wellFormedText): an unknown member named with a lone surrogate
 * must not put one into the server's answer.
 */
export function validate<T>(validator: Validator<T>, value: unknown, path = 'payload'): ValidationResult<T> {
  const issues: Issue[] = [];
  if (validator.check(value, path, issues)) return { ok: true, value: value as T };
  return { ok: false, issues: issues.slice(0, 20).map((i) => ({ path: wellFormedText(i.path), message: wellFormedText(i.message) })) };
}

export function formatIssues(issues: readonly Issue[]): string {
  return issues.map((i) => `${i.path} ${i.message}`).join('; ');
}
